import React, { useState, useEffect } from 'react';
import type { StaffMember, UserRole, WorkType } from '../../types';
import { useSoftKeyboardField } from '../../hooks/useSoftKeyboardField';

interface StaffMemberModalProps {
  member: StaffMember | null;
  userRoles: UserRole[];
  onClose: () => void;
  onSave: (member: StaffMember) => void | Promise<void>;
  /** Contact, IRD & pay blocks: admin/payroll always; managers only when hrDetailsLocked is not set */
  canViewPrivateHrPay?: boolean;
  /** Show "lock HR details" checkbox (admin or payroll viewers only) */
  canSetHrLock?: boolean;
  /** PIN field only (view_staff_pins) */
  canViewSensitive?: boolean;
}

const StaffMemberModal: React.FC<StaffMemberModalProps> = ({
  member,
  userRoles,
  onClose,
  onSave,
  canViewPrivateHrPay = false,
  canSetHrLock = false,
  canViewSensitive = false,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    role: userRoles[0]?.id || '',
    email: '',
    pin: '',
    password: '',
    confirmPassword: '',
    isAdmin: false,
    includeInRoster: true,
    canManageMenu: false,
    canManageCustomers: false,
    irdNumber: '',
    personalEmail: '',
    phone: '',
    address: '',
    visaStatus: '',
    taxCode: '',
    hourlyRate: '',
    annualLeaveRate: '',
    workType: '' as WorkType | '',
    employmentStartDate: '',
    hrDetailsLocked: false,
  });
  const [id, setId] = useState<number | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (member) {
      const roleIsAdminOrTester = member.role?.toLowerCase() === 'admin' || member.role?.toLowerCase() === 'third_party_tester';
      setFormData({
        name: member.name,
        role: member.role,
        email: member.email || '',
        pin: member.pin || '0000',
        password: '',
        confirmPassword: '',
        isAdmin: member.isAdmin || false,
        includeInRoster: roleIsAdminOrTester ? false : (member.includeInRoster !== false),
        canManageMenu: member.canManageMenu === true,
        canManageCustomers: member.canManageCustomers === true,
        irdNumber: member.irdNumber || '',
        personalEmail: member.personalEmail || '',
        phone: member.phone || '',
        address: member.address || '',
        visaStatus: member.visaStatus || '',
        taxCode: member.taxCode || '',
        hourlyRate: member.hourlyRate != null ? String(member.hourlyRate) : '',
        annualLeaveRate: member.annualLeaveRate != null ? String(member.annualLeaveRate) : '',
        workType: (member.workType as WorkType) || '',
        employmentStartDate: member.employmentStartDate || '',
        hrDetailsLocked: member.hrDetailsLocked === true,
      });
      setId(member.id);
    } else {
      const defaultRole = userRoles.find(r => r.id === 'waitstaff')?.id || userRoles[0]?.id || '';
      const defaultIncludeInRoster = true;
      setFormData({
        name: '',
        role: defaultRole,
        email: '',
        pin: '',
        password: '',
        confirmPassword: '',
        isAdmin: false,
        includeInRoster: defaultIncludeInRoster,
        canManageMenu: false,
        canManageCustomers: false,
        irdNumber: '',
        personalEmail: '',
        phone: '',
        address: '',
        visaStatus: '',
        taxCode: '',
        hourlyRate: '',
        annualLeaveRate: '',
        workType: '',
        employmentStartDate: '',
        hrDetailsLocked: false,
      });
      setId(null);
    }
    setErrors({});
  }, [member, userRoles]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (name === 'pin' && !/^\d{0,4}$/.test(value)) {
      return; // Only allow up to 4 digits for PIN
    }
    
    if (name === 'email') {
      setFormData(prev => ({ ...prev, [name]: value.toLowerCase() }));
      if (errors.email) {
        setErrors(prev => ({ ...prev, email: '' }));
      }
    } else if (type === 'checkbox') {
      const target = e.target as HTMLInputElement;
      setFormData(prev => ({ ...prev, [name]: target.checked }));
    } else {
      // If role is changed, clear isAdmin if the new role doesn't allow admin; set includeInRoster false for admin/third_party_tester
      if (name === 'role') {
        const newRole = value.toLowerCase();
        const canBeAdmin = newRole === 'admin' || newRole === 'manager';
        const excludeFromRoster = newRole === 'admin' || newRole === 'third_party_tester';
        setFormData(prev => ({ 
          ...prev, 
          [name]: value,
          isAdmin: canBeAdmin ? prev.isAdmin : false,
          includeInRoster: excludeFromRoster ? false : prev.includeInRoster,
        }));
      } else {
        const updates: Record<string, string | boolean> = { [name]: value };
        // When setting work type: full time & part time → no holiday pay; casual → default 8% if empty
        if (name === 'workType') {
          if (value === 'full_time' || value === 'part_time') {
            updates.annualLeaveRate = '';
          } else if (value === 'casual' && !formData.annualLeaveRate) {
            updates.annualLeaveRate = '0.08';
          }
        }
        setFormData(prev => ({ ...prev, ...updates }));
      }
    }
  };

  // Check if the selected role needs email/password (manager or third_party_tester)
  const roleNeedsEmailPassword = formData.role?.toLowerCase() === 'manager' 
    || formData.role?.toLowerCase() === 'third_party_tester' 
    || formData.isAdmin;
  
  // Check if the selected role can have admin privileges (only admin and manager)
  const roleCanBeAdmin = formData.role?.toLowerCase() === 'admin' 
    || formData.role?.toLowerCase() === 'manager';

  const nameKeyboard = useSoftKeyboardField({
    id: 'staff-modal-name',
    type: 'alphanumeric',
    label: 'Full Name',
    value: formData.name,
    onChange: (v) => setFormData((prev) => ({ ...prev, name: v })),
  });
  const emailKeyboard = useSoftKeyboardField({
    id: 'staff-modal-email',
    type: 'alphanumeric',
    label: 'Email',
    value: formData.email,
    onChange: (v) => setFormData((prev) => ({ ...prev, email: v.toLowerCase() })),
  });
  const pinKeyboard = useSoftKeyboardField({
    id: 'staff-modal-pin',
    type: 'numeric',
    label: 'PIN (4 digits)',
    value: formData.pin,
    onChange: (v) => setFormData((prev) => ({ ...prev, pin: v })),
    manualValidator: (v) => /^\d{0,4}$/.test(v),
    maskValue: true,
  });
  const passwordKeyboard = useSoftKeyboardField({
    id: 'staff-modal-password',
    type: 'alphanumeric',
    label: 'Password',
    value: formData.password,
    onChange: (v) => setFormData((prev) => ({ ...prev, password: v })),
    maskValue: true,
  });
  const confirmPasswordKeyboard = useSoftKeyboardField({
    id: 'staff-modal-confirmPassword',
    type: 'alphanumeric',
    label: 'Confirm Password',
    value: formData.confirmPassword,
    onChange: (v) => setFormData((prev) => ({ ...prev, confirmPassword: v })),
    maskValue: true,
  });

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    // Email is required for managers and third party testers
    if (roleNeedsEmailPassword) {
      if (!formData.email.trim()) {
        const roleName = formData.role?.toLowerCase() === 'third_party_tester' 
          ? 'third party testers' 
          : 'managers';
        newErrors.email = `Email is required for ${roleName}`;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Invalid email address';
      }
    }

    // PIN is required for all staff (4 digits) – but only validated when the current user is allowed to see/edit it
    if (canViewSensitive) {
      if (!formData.pin || formData.pin.length !== 4) {
        newErrors.pin = 'PIN must be exactly 4 digits';
      }
    }

    // Password is required for managers and third party testers (new staff only)
    if (roleNeedsEmailPassword && !id && !formData.password) {
      const roleName = formData.role?.toLowerCase() === 'third_party_tester' 
        ? 'third party testers' 
        : 'managers';
      newErrors.password = `Password is required for new ${roleName}`;
    } else if (formData.password && formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    // Confirm password must match
    if (formData.password && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const pinForSave = canViewSensitive
      ? formData.pin || ''
      : member
        ? member.pin || ''
        : formData.pin || '';

    const base: StaffMember & { password?: string; firestoreId?: string } = {
      name: formData.name,
      role: formData.role,
      email: formData.email,
      pin: pinForSave,
      isAdmin: formData.isAdmin,
      id: id ?? 0,
      includeInRoster: formData.includeInRoster,
      canManageMenu: formData.canManageMenu,
      canManageCustomers: formData.canManageCustomers,
      workType: formData.workType ? (formData.workType as WorkType) : undefined,
      employmentStartDate: formData.employmentStartDate.trim() || undefined,
    };

    let staffData: StaffMember & { password?: string; firestoreId?: string };
    if (!canViewPrivateHrPay && member) {
      staffData = {
        ...base,
        irdNumber: member.irdNumber,
        personalEmail: member.personalEmail,
        phone: member.phone,
        address: member.address,
        visaStatus: member.visaStatus,
        taxCode: member.taxCode,
        hourlyRate: member.hourlyRate,
        annualLeaveRate: member.annualLeaveRate,
        hrDetailsLocked: member.hrDetailsLocked,
      };
    } else {
      const annualLeaveRate =
        formData.workType === 'full_time' || formData.workType === 'part_time'
          ? 0
          : formData.annualLeaveRate !== ''
            ? parseFloat(formData.annualLeaveRate)
            : undefined;
      staffData = {
        ...base,
        irdNumber: formData.irdNumber.trim() || undefined,
        personalEmail: formData.personalEmail.trim() || undefined,
        phone: formData.phone.trim() || undefined,
        address: formData.address.trim() || undefined,
        visaStatus: formData.visaStatus.trim() || undefined,
        taxCode: formData.taxCode.trim() || undefined,
        hourlyRate: formData.hourlyRate !== '' ? parseFloat(formData.hourlyRate) : undefined,
        annualLeaveRate,
        hrDetailsLocked: canSetHrLock ? formData.hrDetailsLocked : (member?.hrDetailsLocked ?? false),
      };
    }
    if (member && (member as { firestoreId?: string }).firestoreId) {
      (staffData as { firestoreId?: string }).firestoreId = (member as { firestoreId?: string }).firestoreId;
    }
    if (formData.password) {
      staffData.password = formData.password;
    }

    try {
      await Promise.resolve(onSave(staffData));
      onClose(); // Close modal on success
    } catch (error: any) {
      // Show user-friendly error messages
      const newErrors: { [key: string]: string } = {};
      const errorMsg = error.message || error.toString();
      if (errorMsg.includes('Email already exists') || errorMsg.includes('email')) {
        newErrors.email = 'This email is already in use';
      }
      if (errorMsg.includes('PIN already exists') || errorMsg.includes('pin')) {
        newErrors.pin = 'This PIN is already in use';
      }
      if (Object.keys(newErrors).length === 0) {
        newErrors._general = errorMsg || 'Failed to save staff member';
      }
      setErrors(newErrors);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-start sm:items-center p-3 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl p-5 sm:p-6 w-full max-w-4xl max-h-[92vh] overflow-y-auto my-2 sm:my-auto">
        <div className="flex items-start justify-between gap-3 mb-4">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">{member ? 'Edit Staff Member' : 'Add New Staff Member'}</h2>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-pink-500"
            aria-label="Close"
            title="Close"
          >
            <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-3">
          <div className="space-y-3 min-w-0">
          <h3 className="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-1">Account</h3>
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <input 
              type="text" 
              name="name" 
              id="name" 
              value={formData.name} 
              className={`w-full px-3 py-2 border rounded-md shadow-sm bg-gray-100 text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500 ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
              data-soft-keyboard-handled
              {...nameKeyboard}
              required 
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>
          <div>
            <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select 
              name="role" 
              id="role" 
              value={formData.role} 
              onChange={handleChange} 
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500 capitalize cursor-pointer"
              style={{ pointerEvents: 'auto' }}
            >
                {userRoles.length > 0 ? (
                  userRoles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)
                ) : (
                  <option value="">No roles available</option>
                )}
            </select>
            {userRoles.length === 0 && (
              <p className="mt-1 text-xs text-red-600">No user roles found. Please configure roles first.</p>
            )}
          </div>
          {roleNeedsEmailPassword && (
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email Address <span className="text-red-500">*</span></label>
              <input 
                type="email" 
                name="email" 
                id="email" 
                value={formData.email} 
                className={`w-full px-3 py-2 border rounded-md shadow-sm bg-gray-100 text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500 ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="user@example.com"
                data-soft-keyboard-handled
                {...emailKeyboard}
                required={roleNeedsEmailPassword}
              />
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
              <p className="mt-1 text-xs text-gray-500">Required for login</p>
            </div>
          )}
          {roleCanBeAdmin && (
            <div>
              <label className="flex items-center space-x-2">
                <input 
                  type="checkbox"
                  name="isAdmin"
                  checked={formData.isAdmin}
                  onChange={handleChange}
                  className="w-5 h-5 text-pink-500 border-gray-300 rounded focus:ring-pink-500"
                />
                <span className="text-sm font-medium text-gray-700">Administrator</span>
              </label>
              <p className="mt-1 text-xs text-gray-500 ml-7">Admins have full access to all features</p>
            </div>
          )}
          {roleNeedsEmailPassword && (
            <>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  {member ? 'New Password (leave blank to keep current)' : 'Password'} {!member && <span className="text-red-500">*</span>}
                </label>
                <div className="relative">
                  <input 
                    type={showPassword ? 'text' : 'password'} 
                    name="password" 
                    id="password" 
                    value={formData.password} 
                    className={`w-full px-3 py-2 border rounded-md shadow-sm bg-gray-100 text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500 ${errors.password ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder={member ? 'Enter new password' : 'Enter password'}
                    data-soft-keyboard-handled
                    {...passwordKeyboard}
                    required={roleNeedsEmailPassword && !member}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
                {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
                {!member && <p className="mt-1 text-xs text-gray-500">Required for login. Minimum 8 characters.</p>}
                {member && formData.password && <p className="mt-1 text-xs text-gray-500">Minimum 8 characters</p>}
              </div>
              {formData.password && (
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                  <input 
                    type={showPassword ? 'text' : 'password'} 
                    name="confirmPassword" 
                    id="confirmPassword" 
                    value={formData.confirmPassword} 
                    className={`w-full px-3 py-2 border rounded-md shadow-sm bg-gray-100 text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500 ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Confirm password"
                    data-soft-keyboard-handled
                    {...confirmPasswordKeyboard}
                  />
                  {errors.confirmPassword && <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>}
                </div>
              )}
            </>
          )}
          {canViewSensitive && (
            <div>
              <label htmlFor="pin" className="block text-sm font-medium text-gray-700 mb-1">
                PIN <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="pin"
                id="pin"
                value={formData.pin}
                maxLength={4}
                className={`w-full px-3 py-2 border rounded-md shadow-sm bg-gray-100 text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500 ${
                  errors.pin ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="4-digit PIN"
                data-soft-keyboard-handled
                {...pinKeyboard}
                required
              />
              {errors.pin && <p className="mt-1 text-sm text-red-600">{errors.pin}</p>}
              <p className="mt-1 text-xs text-gray-500">
                4-digit PIN for POS, Kitchen Display, and Service screens
              </p>
            </div>
          )}
          </div>

          <div className="space-y-3 min-w-0">
          <h3 className="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-1">Roster &amp; POS</h3>
          <div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="includeInRoster"
                checked={formData.includeInRoster}
                onChange={handleChange}
                disabled={formData.role?.toLowerCase() === 'admin' || formData.role?.toLowerCase() === 'third_party_tester'}
                className="w-5 h-5 text-pink-500 border-gray-300 rounded focus:ring-pink-500 disabled:opacity-50"
              />
              <span className="text-sm font-medium text-gray-700">Include in roster</span>
            </label>
            <p className="mt-1 text-xs text-gray-500 ml-7">
              {formData.role?.toLowerCase() === 'admin' || formData.role?.toLowerCase() === 'third_party_tester'
                ? 'Admins and third party testers are not shown in the roster.'
                : 'When checked, this staff appears in the roster grid for shift allocation.'}
            </p>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">POS extras</p>
            <div className="space-y-2">
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="canManageMenu"
                    checked={formData.canManageMenu}
                    onChange={handleChange}
                    className="w-5 h-5 text-pink-500 border-gray-300 rounded focus:ring-pink-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Can manage menu from POS</span>
                </label>
                <p className="mt-0.5 text-xs text-gray-500 ml-7">
                  Shows <span className="font-semibold">MNG MENU</span> on the POS register.
                </p>
              </div>
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="canManageCustomers"
                    checked={formData.canManageCustomers}
                    onChange={handleChange}
                    className="w-5 h-5 text-pink-500 border-gray-300 rounded focus:ring-pink-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Can manage customers from POS</span>
                </label>
                <p className="mt-0.5 text-xs text-gray-500 ml-7">
                  Shows <span className="font-semibold">MNG CUST</span> on the POS register.
                </p>
              </div>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Employment (NZ)</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor="workType" className="block text-sm font-medium text-gray-700 mb-1">Work type</label>
                <select
                  name="workType"
                  id="workType"
                  value={formData.workType}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                >
                  <option value="">— Select —</option>
                  <option value="full_time">Full time</option>
                  <option value="part_time">Part time</option>
                  <option value="casual">Casual</option>
                </select>
              </div>
              <div>
                <label htmlFor="employmentStartDate" className="block text-sm font-medium text-gray-700 mb-1">Start date</label>
                <input
                  type="date"
                  name="employmentStartDate"
                  id="employmentStartDate"
                  value={formData.employmentStartDate}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                />
              </div>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Work type affects holiday pay / leave. Start date is used for NZ leave eligibility.
            </p>
          </div>
          </div>
          </div>

          {member && !canViewPrivateHrPay && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
              <p className="text-sm text-amber-900">
                HR details are locked. Contact an administrator or payroll.
              </p>
            </div>
          )}

          {canViewPrivateHrPay && (
            <div>
              <h3 className="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-1 mb-3">Personal details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="irdNumber" className="block text-sm font-medium text-gray-700 mb-1">
                    IRD number
                  </label>
                  <input
                    type="text"
                    name="irdNumber"
                    id="irdNumber"
                    value={formData.irdNumber}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                    placeholder="e.g. 12-345-678"
                    data-soft-keyboard-handled
                  />
                </div>
                <div>
                  <label htmlFor="personalEmail" className="block text-sm font-medium text-gray-700 mb-1">
                    Personal email
                  </label>
                  <input
                    type="email"
                    name="personalEmail"
                    id="personalEmail"
                    value={formData.personalEmail}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                    placeholder="personal@example.com"
                    data-soft-keyboard-handled
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    id="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                    placeholder="e.g. 021 123 4567"
                    data-soft-keyboard-handled
                  />
                </div>
                <div>
                  <label htmlFor="visaStatus" className="block text-sm font-medium text-gray-700 mb-1">
                    Visa status
                  </label>
                  <input
                    type="text"
                    name="visaStatus"
                    id="visaStatus"
                    value={formData.visaStatus}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                    placeholder="e.g. Citizen, Resident, Work visa"
                    data-soft-keyboard-handled
                  />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <input
                    type="text"
                    name="address"
                    id="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                    placeholder="Street, suburb, city"
                    data-soft-keyboard-handled
                  />
                </div>
              </div>
            </div>
          )}

          {canViewPrivateHrPay && (
            <div>
              <h3 className="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-1 mb-3">Payroll (for payslips)</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="taxCode" className="block text-sm font-medium text-gray-700 mb-1">Tax code (IRD)</label>
                  <select
                    name="taxCode"
                    id="taxCode"
                    value={formData.taxCode}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                  >
                    <option value="">— Select —</option>
                    <option value="M">M (Primary)</option>
                    <option value="ME">ME (Primary, WfF/NZ Super)</option>
                    <option value="M SL">M SL (Primary + Student loan)</option>
                    <option value="ME SL">ME SL (Primary WfF + Student loan)</option>
                    <option value="SB">SB (Secondary $0–$15,600)</option>
                    <option value="S">S (Secondary $15,601–$53,500)</option>
                    <option value="SH">SH (Secondary $53,501–$78,100)</option>
                    <option value="ST">ST (Secondary $78,101–$180,000)</option>
                    <option value="SA">SA (Secondary $180,001+)</option>
                    <option value="SB SL">SB SL</option>
                    <option value="S SL">S SL</option>
                    <option value="SH SL">SH SL</option>
                    <option value="ST SL">ST SL</option>
                    <option value="SA SL">SA SL</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">Default on payslips</p>
                </div>
                <div>
                  <label htmlFor="hourlyRate" className="block text-sm font-medium text-gray-700 mb-1">Hourly rate ($)</label>
                  <input
                    type="number"
                    name="hourlyRate"
                    id="hourlyRate"
                    value={formData.hourlyRate}
                    onChange={handleChange}
                    min={0}
                    step={0.01}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                    placeholder="e.g. 25.50"
                  />
                  <p className="mt-1 text-xs text-gray-500">Default for payslip calculation</p>
                </div>
                {(formData.workType === 'full_time' || formData.workType === 'part_time') ? (
                  <div className="sm:col-span-2 rounded-md bg-gray-50 p-3">
                    <p className="text-sm text-gray-700">Holiday pay does not apply to full time or part time staff. They receive 4 weeks annual leave per year (NZ) instead of pay-as-you-go holiday pay.</p>
                  </div>
                ) : (
                  <div>
                    <label htmlFor="annualLeaveRate" className="block text-sm font-medium text-gray-700 mb-1">Holiday pay rate (e.g. 0.08 = 8%)</label>
                    <input
                      type="number"
                      name="annualLeaveRate"
                      id="annualLeaveRate"
                      value={formData.annualLeaveRate}
                      onChange={handleChange}
                      min={0}
                      max={1}
                      step={0.01}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                      placeholder="e.g. 0.08"
                    />
                    <p className="mt-1 text-xs text-gray-500">NZ casual: typically 8%. Leave blank to use 8%.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {canSetHrLock && (
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="hrDetailsLocked"
                  checked={formData.hrDetailsLocked}
                  onChange={handleChange}
                  className="mt-1 w-5 h-5 text-pink-500 border-gray-300 rounded focus:ring-pink-500"
                />
                <span>
                  <span className="text-sm font-medium text-gray-800">Lock contact, IRD &amp; pay details from managers</span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    Managers can fill these fields until you lock this record. Unlock anytime by editing again and unchecking.
                  </span>
                </span>
              </label>
            </div>
          )}

          {errors._general && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-800 text-sm">{errors._general}</p>
            </div>
          )}
          <div className="flex justify-end space-x-4 border-t border-gray-100 pt-3">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600">Save Member</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StaffMemberModal;